import { sort, equal, readOnly, or } from '@ember/object/computed';
import { computed } from '@ember/object';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { ASSIGNMENT } from '../../../models/assignment';
import {
  POSTING_CREATE_TYPE_NAMES,
  POSTING_CREATE_COVER_EXPENSES_TYPE,
  POSTING_CREATE_DATE_RANGE_TYPE,
  POSTING_CREATE_ESTIMATED_DAY_TYPES,
  POSTING_CREATE_FREELANCER_LEVEL,
  KEY_DATES_OPTIONS
} from 'sh-www/constants/routes/posting-create';
import { ASSIGNMENT_COMMAND_CONSTANTS } from '../../commands/assignment-commands';
import { areCollectionsEqual } from '../../../utils/collections';
import { SERVICES_ELIGIBLE_FOR_SELECT } from '../../../constants/services';
import { SUPPORT } from '../../../constants/links';
import { v1 } from 'ember-uuid';
import { LOCATION } from 'sh-www/models/location';
import { getModelNameForEmberModelInstance } from 'sh-www/utils/misc';

/** The minimum budget amount that qualifies for Select status. */
const MINIMUM_BUDGET_FOR_SELECT = 5000;

/**
 * The maximum amount below the minimum budget for select that will trigger a recommendation to
 * increase the budget to qualify for Select status.
 * */
const MAXIMUM_SELECT_BUDGET_DISCREPANCY_FOR_RECOMMENDATION = 500;

export default Service.extend({
  /**
   * Register Services
   */
  store: service(),
  moment: service(),
  session: service(),
  costCodeCommandsService : service('commands/cost-code-commands'),
  storyCodeCommandsService : service('commands/story-code-commands'),

  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    minimumBudgetForSelect: MINIMUM_BUDGET_FOR_SELECT,

    /**
     * UUID used to prevent duplicate posting creation
     */
    client_uuid: () => v1(),

    /**
     * @type {String} Where was this created from
     */
    createdFrom: ASSIGNMENT.CREATED_FROM.STANDARD,

    /**
     * @type {Assignment|null}    The assignment we're editing or null
     */
    editingPosting: null,

    /**
     * @type {Object}   The object map for errors being returned from the server.
     */
    errors: () => {return {};},

    /**
     * @type {Boolean}      True when the approve posting request is in flight.
     */
    isApprovePostingRequestInFlight: false,

    /**
     * @type {Boolean}      When this is true we DO want to confirm if the user is leaving the experience.
     *                      i.e. set to false to not show the alert when leaving the page.
     */
    isConfirmDiscardData: true,

    /**
     * @type {Boolean}      True when we are sending a create posting request.
     */
    isCreatePostingRequestInFlight: false,

    /**
     * @type {Boolean}      True when we edit posting request is in flight.
     */
    isEditPostingRequestInFlight: false,

    /**
     * @type {Boolean}      True when the request to decline the posting is in flight.
     */
    isDeclinePostingRequestInFlight: false,

    /**
     * @type {Boolean}    True when we have a 'test-matches' api call in flight
     */
    isTestMatchesRequestInFlight: false,

    /**
     * @type {Boolean}    Will be true when we have started the process of making a test assignment live.
     */
    isMakingDraftAssignmentLive: false,

    /**
     * This is a function that will take an assignment and do something (navigate) after a successful draft creation.
     *
     * @type {function(*)}
     */
    onDraftSuccessNavigation: () => {
      return (assignment) => {
        throw new Error('onDraftSuccessNavigation not defined');
      };
    },

    /**
     * Different statuses for the various feedback component types we support
     *
     * @type {String}   of type ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.*
     */
    testMatchesBudgetStatus: ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE,
    testMatchesConfirmationStatus: ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE,
    testMatchesRatingStatus: ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE,
    testMatchesSummaryStatus: ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE,

    /**
     * @type {String}   Various text feedback from 'test-matches' api endpoint.
     */
    testMatchesBudgetText: '',
    testMatchesConfirmationText: '',
    testMatchesRatingText: '',
    testMatchesSummaryText: '',

    /**
     * @type {String}   Hashed value of the previous and current state to determine if we need to make a call to testMatches
     */
    testMatchesCurrentHash: '',
    testMatchesPreviousHash: '',

    /**
     * @type {Number|null}      The low and high budgets as updated by the user.
     */
    updatedBudgetLowValue: null,
    updatedBudgetHighValue: null,

    /**
     * @type {String} Input values for experience chip selector
     */
    updatedClassificationSubjectValue: '',
    updatedClassificationIndustryValue: '',

    /**
     * @type {String} Selections for experience chip selector
     */
    updatedClassificationSubjects: [],
    updatedClassificationIndustries: [],

    /**
     * @type {CompanyTeam|null}    The company team association we're editing or null
     */
    updatedCompanyTeam: null,

    /**
     * @type {Production|null}
     */
    updatedProduction: null,

    /**
     * @type {String|null}      The cloned assignment id or null.
     */
    updatedClonedAssignmentId: null,

    /**
     * @deprecated
     * @type {Ember.MutableArray<Object>}   Array of the option items that have the country names and ids.
     */
    updatedCountryOptions: () => A(),

    /**
     * @type {String}     of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
     */
    updatedCoverExpenses: '',

    /**
     * @type {boolean}     of type POSTING_CREATE_COVER_FEES_TYPE.*
     */
    updatedCoverFees: null,

    /**
     * @type {String}     of type POSTING_CREATE_DATE_RANGE_TYPE.*
     */
    updatedDateRangeType: '',

    /**
     * @type {Moment|null}    This is this the date selected for the assignment deadline
     */
    updatedDateSelected: null,

    /**
     * @type {Array<AssignmentDeliverable>}     Array of assignment deliverables
     */
    updatedDeliverables: [],

    /**
     * @type {String}     This is the updated posting description text
     */
    updatedDescription: '',

    /**
     * @type {String|null}    The freelancer id for direct freelancer posting/project creation.
     */
    updatedDirectFreelancerId: null,

    /**
     * @type {String}     Optional message to freelancer about this direct hire project.
     */
    updatedDirectFreelancerMessage: '',

    /**
     * @type {User|null}    The freelancer user after loading from the server.
     */
    updatedDirectFreelancerUser: null,

    /**
     * @type {Number|null}     The offer amount for direct project creation.
     */
    updatedDirectOfferAmount: null,

    /**
     * @type {Boolean|null}   True or false when the user is ready to make offer or not - null if never selected
     */
    updatedDirectIsReadyToMakeOffer: null,

    /**
     * @type {Boolean|null}    True if the work has been completed for a direct hire. - null if never selected
     */
    updatedDirectIsWorkComplete: null,

    /**
     * @type {Number|null}     Optional cost code for a direct hire.
     */
    updatedDirectCostCode: null,

    /**
     * @type {Number}     The star count that the user has clicked and given a review.
     */
    updatedDirectReviewStarCount: 0,

    /**
     * @type {String}     Optional hire for direct hire review.
     */
    updatedDirectReviewComment: '',

    /**
     * @type {Number|null}     Optional story code for a direct hire.
     */
    updatedDirectStoryCode: null,

    /**
     * @type {String}     This is the custom value the user has entered for estimated number of days.
     */
    updatedEstimatedCustomDays: '',

    /**
     * @type {String}     of type POSTING_CREATE_ESTIMATED_DAY_TYPES.*
     */
    updatedEstimatedDaysTypeEnum: '',

    /**
     * @type {String}     This is the updated posting examples text
     */
    updatedExamples: '',

     /**
     * @type {Boolean}    Clicked to show examples input
     */
    updatedExamplesExplictlyShown: false,

    /**
     * @type {String}     of type POSTING_CREATE_FREELANCER_LEVEL.*
     */
    updatedFreelancerLevel: POSTING_CREATE_FREELANCER_LEVEL.STANDARD,

    /**
     * @type {Boolean} Whether this is a Select posting.
     */
    isSelect: false,

    /**
     * @type {Location|null}    The location model associated with the candidate location
     */
    updatedCandidateLocation: null,

    /**
     * @type {Boolean} Whether to show the key dates input
     */
    updatedHasKeyDates: null,

    /**
     * @type {Location|null}    The location model associated with the location
     */
    updatedLocation: null,

    /**
     * @type {Number}     The assignment type of this new posting.
     */
    updatedPostingType: ASSIGNMENT.TYPES.STANDARD,

    /**
     * @type {Number}   Of type ASSIGNMENT.PRIVACY_LEVEL.*
     */
    updatedPrivacyLevel: null,

    /**
     * @type {Number|null}   Of type ASSIGNMENT.RATE_TYPE.*
     */
    updatedRateType: null,

    /**
     * @type {Ember.MutableArray<Service>}   Array of the services that have been selected.
     */
    updatedSelectedServices: () => A(),

    /**
     * @type {Array<AssignmentSchedule>}     Array of assignment schedule items
     */
    updatedScheduleItems: [],

    /**
     * @type {String}     The title of the assignment that has been updated or is a new title.
     */
    updatedTitle: '',

    /**
     * @type {Boolean} Whether this is posting uses candidate location.
     */
    usingCandidateLocation: false,
  },

  /**
   * @type {Ember.MutableArray<CostCode>}   Array of all cost codes for the company for a direct hire.
   */
  allCompanyCostCodes: A(),

  /**
   * @type {Boolean}    True when we should disable all the form elements on the form.
   */
  areFormElementsDisabled: computed(
  'isApprovePostingRequestInFlight',
  'isCreatePostingRequestInFlight',
  function() {
    return this.get('isCreatePostingRequestInFlight') ||
      this.get('isApprovePostingRequestInFlight');
  }),

   /**
   * @type {Array<Classification>} All available classifications for deciding preferred experience
   */
  availableClassifications: [],

  /**
   * @type {array<Company>}    All available company teams loaded from the server.
   */
  availableCompanyTeams: [],

  /**
   * @type {Ember.MutableArray<Country>}    All available countries loaded from the server.
   */
  availableCountries: A(),

  /**
   * Sort by the 'name' field of the country in ascending order.
   */
  availableCountriesSortNameAscending: ['name'],

  /**
   * @type {Ember.MutableArray<Country>}    All available countries SORTED
   */
  availableCountriesSorted: sort('availableCountries', 'availableCountriesSortNameAscending'),

  /**
   * @type {Ember.MutableArray<Region>}    All available regions loaded from the server.
   */
  availableRegions: A(),

  /**
   * @type {Ember.MutableArray<Service>}    All available Services loaded from the server.
   */
  availableServices: A(),

  /**
   * @type {Boolean}    True if approving assignments is available for the current user.
   */
  canApproveAssignments: equal('currentUser.isAdmin', true),

  /**
   * @type {Array<CompanyMembership>}    Company members
   */
  companyMemberships: [],

  userIdToCompanyTeamsMap: computed('companyMemberships.@each.{companyTeamMemberships,user}', function () {
    return this.companyMemberships.reduce((accum, member) => {
      accum[member.user.id] = member.companyTeamMemberships.map(({ companyTeam }) => companyTeam);
      return accum;
    }, {});
  }),

  /**
   * @type {Boolean}    True if current user is SH admin or company admin
   */
  currentUserIsCompanyAdminOrAdmin: or('currentUser.isCompanyAdmin', 'currentUser.isAdmin'),

  /** @type {Boolean} Company the posting belongs to. */
  buyerCompany: computed(
    'editingPosting.buyer.company',
    'currentUser.company',
  function () {
    return this.editingPosting?.buyer?.company ?? this.currentUser.company;
  }),

  /**
   * An array of all team options. Includes only teams the user is a part of.
   * Admins are part of all teams automatically.
   *
   * @type {Array<CompanyTeam>}
   */
  companyTeamOptions: computed(
    'updatedCompanyTeam.{id,name}',
    'availableCompanyTeams.[]',
    'currentUserIsCompanyAdminOrAdmin',
    'currentUser.id',
    'userIdToCompanyTeamsMap.@each.{id,name}',
  function() {
    const teamOptions = [];
    const currentTeam = this.updatedCompanyTeam;
    const allTeams = this.availableCompanyTeams;

    if (this.currentUserIsCompanyAdminOrAdmin) {
      // enforce the use of teams when available
      teamOptions.push({
        title: 'None',
        value: null,
        selected: currentTeam?.id === null,
      });

      allTeams.forEach((team) => {
        teamOptions.push({
          title: team.name,
          value: team.id,
          selected: currentTeam?.id === team.id,
        });
      });
    } else {
      const currentUserTeams = this.userIdToCompanyTeamsMap[this.currentUser.id] ?? [];
      currentUserTeams.forEach((team) => {
        teamOptions.push({
          title: team.name,
          value: team.id,
          selected: currentTeam?.id === team.id,
        });
      });

      // followers outside this posting's team need dropdown option
      const existingTeamIds = teamOptions.map(({ value }) => value);
      if (currentTeam && !existingTeamIds.includes(currentTeam.id)) {
        teamOptions.push({
          title: currentTeam.name,
          value: currentTeam.id,
          selected: true,
        });
      }
    }

    return teamOptions;
  }),

  /**
   * @type {Boolean}    True if we have any cost codes or story codes available to show.
   */
  hasAnyCostOrStoryCodes: computed(
  'currentUser.company.usesCostCodes',
  function() {
    return this.get('currentUser.company.usesCostCodes');
  }),

  /**
   * @type {Boolean}    True when the user has test matches are passed validation and can submit to create/submit
   *                    an Assignment.
   */
  hasTestedMatchesPassed: computed(
  'testMatchesBudgetStatus',
  'testMatchesRatingStatus',
  'testMatchesSummaryStatus',
  function() {
    return this.get('testMatchesBudgetStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD &&
      this.get('testMatchesRatingStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD &&
      this.get('testMatchesSummaryStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD;
  }),

  /**
   * @type {Boolean}    True when we have all the info we need to submit or publish a new posting.
   */
  isAllInfoSuppliedForNewPosting: computed(
  'updatedTitle',
  'updatedSelectedServices.[]',
  'updatedLocation',
  'updatedPlaceId',
  'updatedPrivacyLevel',
  'updatedDateSelected',
  'updatedDescription',
  'updatedDeliverables.[]',
  'updatedBudgetLowValue',
  'updatedBudgetHighValue',
  'updatedRateType',
  'updatedEstimatedDays',
  function() {
    // Ensure all necessary data is supplied.
    let isSupplied = Boolean(this.get('updatedTitle'))
      && Boolean(this.get('updatedLocation'))
      && Boolean(this.get('updatedDescription'))
      && Boolean(this.get('updatedDeliverables.length'))
      && Boolean(this.get('updatedPrivacyLevel'))
      && Boolean(this.get('updatedBudgetLowValue'))
      && Boolean(this.get('updatedBudgetHighValue'))
      && Boolean(this.get('updatedDateSelected'))
      && Boolean(this.get('updatedSelectedServices.length'))
      && Boolean(this.get('updatedRateType'))
      && Boolean(this.get('updatedEstimatedDays'));

    if (!isSupplied) {
      return false;
    }

    return true;
  }),

  /**
   * This is true when we have any info supplied into our form.
   *
   * @type {Boolean}
   */
  isAnyInfoSuppliedInCreationForm: computed(
  'updatedDateSelected',
  'updatedTitle',
  'updatedSelectedServices.length',
  'updatedLocation',
  'updatedCandidateLocation',
  'updatedPlaceId',
  'updatedDescription',
  'updatedDeliverables.[]',
  'updatedBudgetLowValue',
  'updatedBudgetHighValue',
  'updatedEstimatedDays',
  'updatedFreelancerLevel',
  function() {
    let isAnyInfoSupplied = this.get('updatedTitle') ||
      this.get('updatedSelectedServices.length') ||
      this.get('updatedLocation') ||
      this.get('updatedCandidateLocation') ||
      this.get('updatedDescription') ||
      this.get('updatedDeliverables.length') ||
      this.get('updatedBudgetLowValue') ||
      this.get('updatedBudgetHighValue') ||
      this.get('updatedEstimatedDays') ||
      this.get('updatedDateSelected') ||
      this.get('updatedFreelancerLevel');

    if (isAnyInfoSupplied) {
      return true;
    }

    return false;
  }),

  /**
   * @type {Boolean}      True when the updated deadline date has expired.
   */
  isDeadlineDateExpired: computed('updatedDateSelected', function() {
    if (!this.updatedDateSelected) {
      return false;
    }

    const momentService = this.moment;
    const now = momentService.moment();
    return this.updatedDateSelected.toDate().getTime() < now.toDate().getTime();
  }),

  /**
   * @type {Boolean}    true if we are in the direct hire mode.
   */
  isDirectHireMode: computed('updatedDirectFreelancerId', function() {
    return this.get('updatedDirectFreelancerId');
  }),

  /**
   * @type {Boolean}    True when the existing posting as changed and needs saving.
   */
  isExistingPostingUpdated: computed(
  'editingPosting',
  'updatedCompanyTeamId',
  'updatedTitle',
  'updatedSelectedServices.[]',
  'updatedLocation',
  'updatedCandidateLocation',
  'updatedPlaceId',
  'updatedDateRangeType',
  'updatedDescription',
  'updatedDeliverables.[]',
  'updatedExamples',
  'updatedScheduleItems.[]',
  'updatedPrivacyLevel',
  'updatedBudgetLowValue',
  'updatedBudgetHighValue',
  'updatedDateSelected',
  'updatedEstimatedDays',
  'updatedCoverExpenses',
  'updatedFreelancerLevel',
  'updatedRateTypeParsed',
  function() {
    let assignment = this.get('editingPosting');
    if (!assignment) {
      return false;
    }

    let assignmentExpensesEnum = assignment.get('isAllowsExpenses') ?
      POSTING_CREATE_COVER_EXPENSES_TYPE.YES :
      POSTING_CREATE_COVER_EXPENSES_TYPE.NO;

    return assignment.get('compensationMin') !== this.get('updatedBudgetLowValue') ||
      assignment.get('compensationMax') !== this.get('updatedBudgetHighValue') ||
      assignment.get('title') !== this.get('updatedTitle') ||
      assignment.get('description') !== this.get('updatedDescription') ||
      assignment.get('examples') !== this.get('updatedExamples') ||
      assignment.get('privacyLevel') !== this.get('updatedPrivacyLevel') ||
      assignment.get('rateType') !== this.get('updatedRateTypeParsed') ||
      assignment.get('estimatedDays') !== this.get('updatedEstimatedDays') ||
      assignment.production?.id !== this.updatedProduction?.id ||
      assignment.companyTeam?.id !== this.updatedCompanyTeam?.id ||
      assignmentExpensesEnum !== this.get('updatedCoverExpenses') ||
      assignment.sellerFeesCovered !== this.updatedCoverSellerFees ||
      !areCollectionsEqual(assignment.get('assignmentDeliverables'), this.get('updatedDeliverables')) ||
      !areCollectionsEqual(assignment.get('services'), this.get('updatedSelectedServices')) ||
      !areCollectionsEqual(assignment.get('assignmentSchedules'), this.get('updatedScheduleItems')) ||
      this._hasLocationChanged() ||
      this._hasCandidateLocationChanged() ||
      this._hasAssignmentDeadlineDateChanged() ||
      this._hasFreelancerLevelChanged();
  }),

  isCandidateGlobal: computed(
    'updatedCandidateLocation.locationType',
    function () {
      return this.updatedCandidateLocation?.locationType === LOCATION.TYPES.GLOBAL;
    }
  ),

  isGlobal: computed(
    'updatedLocation.locationType',
    function () {
      return this.updatedLocation?.locationType === LOCATION.TYPES.GLOBAL;
    }
  ),

  /** @type {Boolean} Whether to make a call to testMatches before creating the Assignment. */
  isNeedsTestMatches: computed(
    'updatedPrivacyLevel',
    'canApproveAssignments',
    'testMatchesPreviousHash',
    'testMatchesCurrentHash',
    'isSelect',
    'isEligibleForSelect',
    function () {
      if (this.get('isSelect') || this.get('isEligibleForSelect')) {
        return false;
      }

      if (
        this.get('canApproveAssignments')
        || this.get('updatedPrivacyLevel') !== ASSIGNMENT.PRIVACY_LEVEL.PUBLIC
      ) {
        return false;
      }

      const currentHash = this.get('testMatchesCurrentHash');
      const previousHash = this.get('testMatchesPreviousHash');

      return !previousHash || currentHash !== previousHash;
    },
  ),

  /**
   * @type {Boolean}    True if the user has selected true or false about ready to make an offer.
   */
  isReadyToMakeOfferSelected: computed('updatedDirectIsReadyToMakeOffer', function() {
    return this.get('updatedDirectIsReadyToMakeOffer') != null;
  }),

  /**
   * @type {Boolean}      True when testMatches has a status that should 'BLOCK' a user from submitting anything.
   */
  isTestMatchesBlocked: computed(
  'testMatchesSummaryStatus',
  function() {
    return this.get('testMatchesSummaryStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.BLOCK;
  }),

  /**
   * @type {Boolean}    True when we have all info needed to make a call to `test-matches`
   */
  isTestMatchesAllInfoSupplied: computed(
  'updatedSelectedServices.length',
  'updatedLocation',
  'updatedDateSelected',
  'updatedPlaceId',
  'updatedRateType',
  'updatedBudgetLowValue',
  'updatedBudgetHighValue',
  'updatedEstimatedDays',
  'updatedFreelancerLevel',
  function() {
    const isSupplied = this.get('updatedSelectedServices.length') &&
      this.get('updatedDateSelected') &&
      this.get('updatedRateType') &&
      this.get('updatedLocation') &&
      this.get('updatedBudgetLowValue') &&
      this.get('updatedBudgetHighValue') &&
      this.get('updatedEstimatedDays') &&
      this.get('updatedFreelancerLevel');

    if (!isSupplied) {
      return false;
    }

    let deadlineToday = this.get('moment').moment().hours(23).minutes(59).seconds(59).milliseconds(0),
      deadline = this.get('updatedDateSelected');
    if (deadline.toDate().getTime() < deadlineToday.toDate().getTime()) {
      // don't test-blast if the deadline is before today.
      return false;
    }

    if (parseInt(this.get('updatedBudgetLowValue')) > parseInt(this.get('updatedBudgetHighValue'))) {
      return false;
    }

    return true;
  }),

  /**
   * @type {Boolean}    True if the user has selected true or false about if the work has been complete for direct hire.
   */
  isWorkCompleteSelected: computed('updatedDirectIsWorkComplete', function() {
    return this.get('updatedDirectIsWorkComplete') != null;
  }),

  /**
   * @type {String}    Return a location type for all related models.
   */
  candidateLocationModelType: computed(
    'updatedCandidateLocation.locationType',
    function () {
      const { updatedCandidateLocation } = this;
      if (!updatedCandidateLocation) return '';

      return updatedCandidateLocation.locationType ?? getModelNameForEmberModelInstance(updatedCandidateLocation);
    }
  ),

  /**
   * @type {String}    Return a location type for all related models.
   */
  locationModelType: computed(
    'updatedLocation.locationType',
    function () {
      const { updatedLocation } = this;
      if (!updatedLocation) return '';

      return updatedLocation.locationType ?? getModelNameForEmberModelInstance(updatedLocation);
    }
  ),

  /** @type {String} The URL of the Select help page. */
  selectHelpLink: computed('currentUser.isBuyer', function () {
    return this.get('currentUser.isBuyer')
      ? SUPPORT.SELECT_FOR_BUYERS
      : SUPPORT.SELECT_FOR_SELLERS;
  }),

  /** @type {Boolean} Whether the privacy selection qualifies for Select status. */
  privacyEligibleForSelect: computed(
    'editingPosting',
    'updatedPrivacyLevel',
    function () {
      // If published, always qualifies, otherwise must be public
      return !!this.get('editingPosting') || this.get('updatedPrivacyLevel') === ASSIGNMENT.PRIVACY_LEVEL.PUBLIC;
    }
  ),

  /** @type {Boolean} Whether the services selection qualifies for Select status. */
  servicesEligibleForSelect: computed(
    'privacyEligibleForSelect',
    'updatedSelectedServices.[]',
    function () {
      if (!this.get('privacyEligibleForSelect')) return false;

      const services = this.get('updatedSelectedServices');
      return services.any((service) => service.get('id') in SERVICES_ELIGIBLE_FOR_SELECT);
    },
  ),

  /** @type {Boolean} Whether the budget selection qualifies for Select status. */
  budgetEligibleForSelect: computed(
    'privacyEligibleForSelect',
    'isFlatFee',
    'updatedEstimatedDays',
    'additionalBudgetRequiredForSelect',
    function () {
      if (!this.get('privacyEligibleForSelect')) return false;

      const isFlatFee = this.get('isFlatFee');
      const estimatedDays = this.get('updatedEstimatedDays');

      // If this is a day rate posting, wait for the estimated days value.
      if (!isFlatFee && !estimatedDays) return false;

      const additionalBudgetRequired = this.get('additionalBudgetRequiredForSelect');
      return additionalBudgetRequired <= 0;
    },
  ),

  /** @type {Boolean} Whether the budget selection almost qualifies for Select status. */
  budgetAlmostEligibleForSelect: computed(
    'isFlatFee',
    'updatedEstimatedDays',
    'additionalBudgetRequiredForSelect',
    function () {
      const isFlatFee = this.get('isFlatFee');
      const estimatedDays = this.get('updatedEstimatedDays');

      // If this is a day rate posting, wait for the estimated days value.
      if (!isFlatFee && !estimatedDays) return false;

      const additionalBudgetRequired = this.get('additionalBudgetRequiredForSelect');
      const tolerance = MAXIMUM_SELECT_BUDGET_DISCREPANCY_FOR_RECOMMENDATION;

      return isFlatFee
        ? additionalBudgetRequired <= tolerance
        : additionalBudgetRequired * estimatedDays <= tolerance;
    },
  ),

  /** @type {Number} The additional amount required to qualify for Select status. */
  additionalBudgetRequiredForSelect: computed(
    'isFlatFee',
    'updatedBudgetHighValue',
    'updatedEstimatedDays',
    function () {
      const requiredBudget = MINIMUM_BUDGET_FOR_SELECT;
      const estimatedDays = this.get('updatedEstimatedDays');
      const budget = this.get('updatedBudgetHighValue');

      return this.get('isFlatFee')
        ? requiredBudget - budget
        : (requiredBudget - (estimatedDays * budget)) / estimatedDays;
    },
  ),

  /** @type {Boolean} Whether the posting is flat fee. */
  isFlatFee: computed('updatedRateTypeParsed', function () {
    return this.get('updatedRateTypeParsed') === ASSIGNMENT.RATE_TYPE.FLAT;
  }),

  /** @type {Boolean} Whether the posting qualifies for Select status. */
  isEligibleForSelect: computed(
    'isSelect',
    'privacyEligibleForSelect',
    'servicesEligibleForSelect',
    'budgetEligibleForSelect',
    function () {
      // The posting is not eligible for Select if it is already Select.
      if (this.get('isSelect')) return false;

      return this.get('privacyEligibleForSelect')
        && this.get('servicesEligibleForSelect')
        && this.get('budgetEligibleForSelect');
    },
  ),

  /**
   * This is the actual VALUE of the estimated days, so from 1, 2, 3 ... up to custom number
   *
   * @type {Number}
   */
  updatedEstimatedDays: computed(
    'updatedEstimatedCustomDays',
    'updatedEstimatedDaysTypeEnum',
    function() {
    let wholeDayRegex = new RegExp(POSTING_CREATE_ESTIMATED_DAY_TYPES.WHOLE_DAY),
      estimatedDayTypeEnum = this.get('updatedEstimatedDaysTypeEnum'),
      updatedEstimatedCustomDays = this.get('updatedEstimatedCustomDays');

    if (wholeDayRegex.test(estimatedDayTypeEnum)) {
      return parseInt(estimatedDayTypeEnum.split('_')[2]);
    }

    return parseInt(updatedEstimatedCustomDays);
  }),

  /**
   * @type {Boolean}    True if the user has made it an urgent assignment.
   */
  updatedIsUrgent: equal('updatedDateRangeType', POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS),

  /**
   * @type {Number}     The minimum rating ready to send down to the API.
   */
  updatedMinRating: computed('updatedFreelancerLevel', function() {
    switch (this.get('updatedFreelancerLevel')) {
      case POSTING_CREATE_FREELANCER_LEVEL.BASIC:
        return 1;
      case POSTING_CREATE_FREELANCER_LEVEL.STANDARD:
        return 3;
      case POSTING_CREATE_FREELANCER_LEVEL.PREMIUM:
        return 4;
    }

    return 0;
  }),

  /**
   * @type {Number}   Id of the company team this posting is associated with
   */
  updatedCompanyTeamId: readOnly('updatedCompanyTeam.id'),

  /**
   * @type {String}   Google place id of the city the user has selected.
   */
  updatedPlaceId: readOnly('updatedLocation.placeId'),

  /**
   * @type {String}   Google place id of the city the user has selected.
   */
  updatedCandidatePlaceId: readOnly('updatedCandidateLocation.placeId'),

  /**
   * @type {Array} Combined list of all preferred experience options selected
   */
  combinedClassificationOptions: computed(
  'updatedClassificationSubjects.[]',
  'updatedClassificationIndustries.[]',
  function() {
    return this.updatedClassificationSubjects.concat(this.updatedClassificationIndustries);
  }),

  /**
   * @type {number[]} Custom classifications given to this assignment
   */
  updatedClassificationIds: computed(
  'combinedClassificationOptions.[]',
  function() {
    return this.combinedClassificationOptions.reduce((accum, option) => {
      if (!option.isCustom) {
        accum.push(Number.parseInt(option.model));
      }

      return accum;
    }, []);
  }),


  /**
   * @type {string[]} Custom classifications given to this assignment
   */
  updatedCustomClassifications: computed(
  'combinedClassificationOptions.[]',
  function() {
    return this.combinedClassificationOptions.reduce((accum, option) => {
      if (option.isCustom) {
        accum.push({
          label: option.key,
          category: option.category,
        });
      }

      return accum;
    }, []);
  }),

  /**
   * @type {String} The more easily readable identifier for the posting type.
   */
  updatedPostingTypeName: computed(
    'currentUser.isAdmin',
    'editingPosting.createdFrom',
    'updatedPostingType',
    'isSelect',
    function () {
      // 'Select' is treated like a type due to the significant changes it is associated with.
      if (
        !this.get('currentUser.isAdmin')
        && (
          this.get('editingPosting.createdFrom') === ASSIGNMENT.CREATED_FROM.CREATE
          || (!this.get('editingPosting') && this.get('isSelect'))
        )
      ) {
        return 'select';
      }

      return POSTING_CREATE_TYPE_NAMES[this.get('updatedPostingType')];
    }
  ),

  /**
   * @type {Number}     This will keep the rate type parsed as a Number.  Need this because of lsg-select bug converts to string.
   */
  updatedRateTypeParsed: computed('updatedRateType', function() {
    return parseInt(this.get('updatedRateType'));
  }),

  updatedDeliverablesParsed: computed('updatedDeliverables.@each.description', function() {
    return this.updatedDeliverables.reduce((accum, { description }) => {
      if (description) {
        accum.push(description);
      }

      return accum;
    }, []);
  }),

  get scheduleItemTypeIsCustomMap() {
    return KEY_DATES_OPTIONS.reduce((accum, option) => {
      accum[option.model] = option.custom;

      return accum;
    }, {});
  },

  updatedScheduleItemsParsed: computed(
    'updatedHasKeyDates',
    'updatedScheduleItems.@each.{type,description,date}',
  function() {
    // clear draft work on submit if 'No' selected
    if (!this.updatedHasKeyDates) {
      return [];
    }

    const { scheduleItemTypeIsCustomMap } = this;

    return this.updatedScheduleItems.reduce((accum, { type, description, date }) => {
      const custom = Boolean(scheduleItemTypeIsCustomMap[type]);

      // custom input with nothing else paired with it
      if (!description && !date && custom) {
        return accum;
      }

      if (type || description || date) {
        accum.push({
          type,
          description,
          date,
          custom,
        });
      }

      return accum;
    }, []);
  }),

  /**
   * @type {CompanySellerConnection}
   */
  currentCompanyConnection: computed(
    'currentUser.company.id',
    'updatedDirectFreelancerUser.companySellerConnections.@each.companyId',
    function() {
      const currentUserCompanyId = this.currentUser.company?.id;
      if (!currentUserCompanyId) {
        return null;
      }

      return this.updatedDirectFreelancerUser?.companySellerConnections.find(({ companyId }) => currentUserCompanyId == companyId);
    }
  ),

  /**
   * @type {Boolean} Whether creator is in network.
   */
  isInNetwork: computed(
    'currentCompanyConnection',
    function() {
      return this.currentCompanyConnection?.invited ?? false;
    }
  ),

  /**
   * @type {Boolean} Whether to show the cover seller fees field.
   */
  showSellerFeesField: computed(
    'buyerCompany.alwaysCoversCreatorFees',
    function() {
      return !this.buyerCompany.alwaysCoversCreatorFees;
    }
  ),

  /**
   * @type {Boolean} Whether to show the cover seller fees field.
   */
  showSellerFeesFieldProject: computed(
    'isInNetwork',
    'buyerCompany.alwaysCoversCreatorFees',
    function() {
      return !this.buyerCompany.alwaysCoversCreatorFees
        && !this.isInNetwork;
    }
  ),

  /**
   * @type {Boolean} Whether to show the expenses field.
   */
  showExpensesField: computed(
    'currentUser.isAdmin',
    'currentUser.company.canEnableExpenses',
    function () {
      if (this.currentUser.isAdmin) return true;
      return this.get('currentUser.company.canEnableExpenses');
    },
  ),

  /**
   * This will compare the deadline date of the assignment with the edit posting state that the user has modified.
   *
   * @return {boolean}
   * @private
   */
  _hasAssignmentDeadlineDateChanged() {
    let assignment = this.get('editingPosting');
    if (!assignment || !assignment.get('deadline')) {
      return false;
    }

    let updatedDateSelected = this.get('updatedDateSelected');
    if (!updatedDateSelected) {
      return false;
    }

    return updatedDateSelected.toDate().getTime() !== assignment.get('deadline').getTime();
  },

  /**
   * Compare assignment state with user updated state to see if the user has changed this.
   *
   * @return {boolean}
   * @private
   */
  _hasFreelancerLevelChanged() {
    let assignment = this.get('editingPosting');
    if (!assignment) {
      return false;
    }

    let level = POSTING_CREATE_FREELANCER_LEVEL.BASIC;
    switch (assignment.get('minRating')) {
      case 1:
      case 2:
        level = POSTING_CREATE_FREELANCER_LEVEL.BASIC;
        break;
      case 3:
      case 4:
        level = POSTING_CREATE_FREELANCER_LEVEL.STANDARD;
        break;
      case 5:
        level = POSTING_CREATE_FREELANCER_LEVEL.PREMIUM;
        break;
    }

    return level !== this.get('updatedFreelancerLevel');
  },

  /**
   * Compare assignment state with user updated state to see if there has been a change.
   *
   * @return {boolean}
   * @private
   */
  _hasCandidateLocationChanged() {
    const assignment = this.editingPosting;
    if (!assignment) return false;

    const addedCandidateLocation = !assignment.candidateLocation && this.updatedCandidateLocation;
    const removedCandidateLocation = assignment.candidateLocation && !this.updatedCandidateLocation;
    if (addedCandidateLocation || removedCandidateLocation) {
      // added or removed the location
      return true;
    } else if (this.updatedCandidateLocation.locationType === LOCATION.TYPES.GLOBAL) {
      if (assignment.candidateLocation.locationType !== LOCATION.TYPES.GLOBAL) {
        // set to global
        return true;
      }
    }

    if (
      this.candidateLocationModelType === LOCATION.TYPES.COUNTRY
      || this.candidateLocationModelType === LOCATION.TYPES.REGION
    ) {
      return this.updatedCandidateLocation.id === assignment.candidateLocation?.id;
    }

    return this.updatedCandidateLocation?.placeId === assignment.candidateLocation?.placeId;
  },

  /**
   * Compare assignment state with user updated state to see if there has been a change.
   *
   * @return {boolean}
   * @private
   */
  _hasLocationChanged() {
    let assignment = this.get('editingPosting');
    if (!assignment) {
      return false;
    }

    const addedLocation = !assignment.location && this.updatedLocation;
    const removedLocation = assignment.location && !this.updatedLocation;
    if (addedLocation || removedLocation) {
      // added or removed the location
      return true;
    } else if (this.updatedLocation.locationType === LOCATION.TYPES.GLOBAL) {
      if (assignment.location.locationType !== LOCATION.TYPES.GLOBAL) {
        // set to global
        return true;
      }
    }

    if (
      this.locationModelType === LOCATION.TYPES.COUNTRY
      || this.locationModelType === LOCATION.TYPES.REGION
    ) {
      return this.updatedLocation.id === assignment.location?.id;
    }

    return this.updatedLocation?.placeId === assignment.location?.placeId;
  },

  initializeForcedProperties() {
    if (this.buyerCompany.alwaysCoversCreatorFees) {
      this.set('updatedCoverSellerFees', true);
    }
  },

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    initializePropertyDefaults(this);
  },
});
